import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, FileText, AlertTriangle, DollarSign } from 'lucide-react'; // Added DollarSign icon
import {
  clearError,
  clearSelectedDateEvents,
  fetchScheduleEvents,
  selectScheduleCurrentDate,
  selectScheduleError,
  selectScheduleEvents,
  selectScheduleLastFetchedKey,
  selectScheduleLoading,
  selectScheduleSelectedDate,
  selectScheduleSelectedDateEvents,
  setCurrentDate,
  setError,
  setSelectedDate,
  setSelectedDateEvents,
} from '../store/scheduleCalendarSlice.js';

const ScheduleCalendar = ({ user, onRecordSelect, api, recordCache = {} }) => {
  const dispatch = useDispatch();
  const currentDate = useSelector(selectScheduleCurrentDate);
  const events = useSelector(selectScheduleEvents);
  const lastFetchedKey = useSelector(selectScheduleLastFetchedKey);
  const selectedDate = useSelector(selectScheduleSelectedDate);
  const selectedDateEvents = useSelector(selectScheduleSelectedDateEvents);
  const loading = useSelector(selectScheduleLoading);
  const error = useSelector(selectScheduleError);
  const eventsListRef = useRef(null); // Ref for the events list section

  const fetchMonthEvents = useCallback(async () => {
    dispatch(clearError());
    
    // --- FIX: Use UTC boundaries for the query ---
    // Previously, using local dates (new Date(y, m, 1)) and converting to ISOString
    // would incorrectly filter out events on the 1st.
    // Using Date.UTC ensures we request the absolute start of the UTC day.
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const startDate = new Date(Date.UTC(year, month, 1, 0, 0, 0));
    const endDate = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));
    // --- END FIX ---
    
    const fetchKey = `${startDate.toISOString()}|${endDate.toISOString()}`;
    if (fetchKey === lastFetchedKey && events.length > 0) {
      return;
    }

    try {
      await dispatch(
        fetchScheduleEvents({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        })
      ).unwrap();
    } catch (err) {
      console.error('Failed to fetch events:', err);
      dispatch(setError('Failed to load scheduled events'));
    }
  }, [currentDate, dispatch, events.length, lastFetchedKey]);

  useEffect(() => {
    fetchMonthEvents();
  }, [fetchMonthEvents]);

  // This effect will smoothly scroll to the event list when a date is clicked
  useEffect(() => {
    if (selectedDate && eventsListRef.current) {
      eventsListRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedDate, selectedDateEvents]); // Run when date or events change

  // This function creates a key from a date's LOCAL parts
  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) { days.push(null); }
    for (let i = 1; i <= daysInMonth; i++) { days.push(i); }
    return days;
  };

  const getEventsForDay = useCallback((day) => {
    if (!day) return [];
    
    // 1. Create the date key for the *local* calendar day.
    // e.g., "2025-11-20"
    const dateKey = formatDateKey(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    
    // Map event's UTC date to a local date for comparison
    return events.filter(event => {
      if (!event.scheduledDate) return false;
      
      // This is the server's UTC string
      const eventDateObj = new Date(event.scheduledDate); 
      
      // 2. Create a *new local Date object* using the *UTC parts* of the server date.
      // This is the "wall time" date, represented in the local timezone.
      const localDateFromUtcParts = new Date(
        eventDateObj.getUTCFullYear(), 
        eventDateObj.getUTCMonth(), 
        eventDateObj.getUTCDate()
      );
      
      // 3. Create a date key from this new local object.
      // formatDateKey will use local getters (getFullYear, etc.) on this new date.
      const eventDateKey = formatDateKey(localDateFromUtcParts);
      
      // 4. Compare the two keys.
      return eventDateKey === dateKey;
    });
  }, [currentDate, events]);

  // Update selected events list when new events are fetched (handles month switching via Today button)
  useEffect(() => {
    if (selectedDate && events.length > 0) {
        // Ensure we are selecting from the currently loaded month's events
        const day = selectedDate.getDate();
        if (selectedDate.getMonth() === currentDate.getMonth() && selectedDate.getFullYear() === currentDate.getFullYear()) {
            dispatch(setSelectedDateEvents(getEventsForDay(day)));
        }
    }
  }, [dispatch, events, selectedDate, currentDate, getEventsForDay]);

  const isPastDue = (event) => {
    if (!event || !event.scheduledDate) return false;
    if (event.isCompleted) return false; 

    const now = new Date(); // Current local time
    
    // This is the "wall time" logic
    const eventDateObj = new Date(event.scheduledDate);
    const eventDate = new Date(eventDateObj.getUTCFullYear(), eventDateObj.getUTCMonth(), eventDateObj.getUTCDate());

    if (event.scheduledTime) {
      const [hours, minutes] = event.scheduledTime.split(':').map(Number);
      // The event's "wall time" end
      const eventEndTime = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), hours, (minutes || 0) + 15);
      return now > eventEndTime;
    }

    // The end of the event's "wall time" day
    const endOfDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), 23, 59, 59);
    return now > endOfDay;
  };
  
  const handleTodayClick = () => {
    const today = new Date();
    dispatch(setCurrentDate(today));
    dispatch(setSelectedDate(today));
    
    // If we are already in the current month, map events immediately.
    // If switching months, the useEffect on [events] will handle the mapping after fetch.
    if (today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear()) {
        dispatch(setSelectedDateEvents(getEventsForDay(today.getDate())));
    }
  };

  const handlePrevMonth = () => {
    dispatch(setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)));
    dispatch(setSelectedDate(null));
    dispatch(clearSelectedDateEvents());
  };
  
  const handleNextMonth = () => {
    dispatch(setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)));
    dispatch(setSelectedDate(null));
    dispatch(clearSelectedDateEvents());
  };

  const handleDateClick = (e, day) => {
    e.preventDefault(); 
    if (!day) return;
    // clickedDate is a local date
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    dispatch(setSelectedDate(clickedDate));
    dispatch(setSelectedDateEvents(getEventsForDay(day)));
  };

  const handleEventClick = async (e, eventData) => {
    e.preventDefault(); 

    // --- (START) LOCKING LOGIC (Section 1 of 2) ---
    // To re-lock overdue tasks, UNCOMMENT the 'if' block below.
    // -----------------------------------------------------
    // if (String(user.role).toLowerCase() === 'collector' && isPastDue(eventData)) { 
    //    setError('This task is overdue and locked. An administrator can unlock it by adding a new follow-up task.');
    //    setTimeout(() => setError(''), 5000); // Clear error after 5s
    //    return;
    // }
    // --- (END) LOCKING LOGIC (Section 1 of 2) ---

    const cachedRecord = recordCache?.[eventData.recordId];
    if (cachedRecord) {
      onRecordSelect(cachedRecord);
    }

    try {
      const response = await api.get(`/records/${eventData.recordId}`);
      onRecordSelect(response.data);
    } catch (err) {
      console.error('Failed to fetch record:', err);
      if (!cachedRecord) {
        dispatch(setError('Failed to open record details'));
      }
    }
  };

  const getStatusColor = (status) => ({
      'callback': 'bg-blue-100 text-blue-800', 'lvm': 'bg-yellow-100 text-yellow-800',
      'spoke_to': 'bg-green-100 text-green-800', 'sent_email_fax': 'bg-purple-100 text-purple-800',
      'offer': 'bg-indigo-100 text-indigo-800', 'settle': 'bg-pink-100 text-pink-800',
      'wfp': 'bg-orange-100 text-orange-800', 'payment_received': 'bg-emerald-100 text-emerald-800',
      'closed': 'bg-red-100 text-red-800'
  }[status] || 'bg-gray-100 text-gray-800');

  const formatStatusLabel = (status) => ({
      'callback': 'Call Back', 'lvm': 'Left Voice Message', 'spoke_to': 'Spoke To',
      'sent_email_fax': 'Sent Email/Fax', 'offer': 'Offer', 'settle': 'Settle',
      'wfp': 'Waiting for Payment', 'payment_received': 'Payment Received', 'closed': 'Closed'
  }[status] || status);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const isTodayCell = (day) => {
    if (!day) return false;
    const todayDate = new Date(); // Browser's local "today"
    return day === todayDate.getDate() && 
            currentDate.getMonth() === todayDate.getMonth() && 
            currentDate.getFullYear() === todayDate.getFullYear();
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2"><Calendar className="w-6 h-6" />Schedule Calendar</h2>
        <div className="flex items-center gap-4">
          <button 
            onClick={handleTodayClick}
            className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-md text-sm font-medium hover:bg-indigo-100 transition-colors"
          >
            Today
          </button>
          <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><ChevronLeft className="w-5 h-5" /></button>
          <h3 className="text-lg font-semibold text-gray-700 min-w-[150px] text-center">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
          <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">{error}</div>}

      {loading ? (
        <div className="flex justify-center items-center h-64"><div className="text-gray-500">Loading events...</div></div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center font-semibold text-gray-600 py-2">{day}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-1 mb-6">
            {getDaysInMonth().map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const hasEvents = dayEvents.length > 0;
              const hasPastDue = dayEvents.some(e => isPastDue(e));
              return (
                <div 
                  key={index} 
                  onClick={(e) => handleDateClick(e, day)} 
                  className={`min-h-[90px] p-2 border rounded-lg cursor-pointer transition-all flex flex-col ${!day ? 'bg-gray-50 cursor-default' : 'hover:bg-gray-50'} ${isTodayCell(day) ? 'bg-blue-50 border-blue-400' : 'border-gray-200'} ${hasEvents && !isTodayCell(day) ? 'bg-yellow-50' : ''} ${selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === currentDate.getMonth() ? 'ring-2 ring-indigo-500' : ''}`}
                >
                  {day && (
                    <>
                      <div className="font-medium text-sm mb-1 self-end">{day}</div>
                      {hasEvents && (
                        <div className="mt-auto space-y-1 text-center">
                          <div className="text-xs font-semibold text-gray-700 bg-gray-200 rounded-full px-2 py-0.5">
                            {dayEvents.length} event{dayEvents.length > 1 ? 's' : ''}
                          </div>
                          {hasPastDue && (
                              String(user.role).toLowerCase() === 'collector' ? ( 
                                <div className="text-xs text-red-600 font-bold flex items-center justify-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> Past due
                                </div>
                              ) : (
                                <div className="text-xs text-orange-600 font-medium flex items-center justify-center gap-1" title="An event for a collector is overdue">
                                    <AlertTriangle className="w-3 h-3" /> Overdue
                                </div>
                              )
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {selectedDate && (
            <div className="border-t pt-6" ref={eventsListRef}>
              {/* This correctly displays the LOCAL date you clicked on */}
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Events for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h3>
              {selectedDateEvents.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {selectedDateEvents.map((event, idx) => {

                    // --- (START) LOCKING LOGIC (Section 2 of 2) ---
                    // To re-lock overdue tasks, COMMENT OUT the line `const isLocked = false;`
                    // and UNCOMMENT the line below it.
                    // -----------------------------------------------------
                    const isLocked = false; // CURRENT: Button is always enabled
                    // const isLocked = String(user.role).toLowerCase() === 'collector' && isPastDue(event); 
                    // --- (END) LOCKING LOGIC (Section 2 of 2) ---

                    return (
                        <div key={`${event.recordId}-${event.commentId}-${idx}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                        <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>{formatStatusLabel(event.status)}</span>
                                {event.scheduledTime && <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{event.scheduledTime}</span>}
                                {isPastDue(event) && <span className="text-xs text-red-600 font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Overdue</span>}
                            </div>

                            {/* --- NEW: Display Offer Amount if status is offer --- */}
                            {event.status === 'offer' && event.offerAmount && (
                                <div className="mb-2 inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-bold rounded border border-green-200">
                                    <DollarSign className="w-3 h-3 mr-1" />
                                    Offer: ${event.offerAmount}
                                </div>
                            )}
                            {/* --------------------------------------------------- */}

                            <div className="text-sm text-gray-700 mb-2">{event.text}</div>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span className="flex items-center gap-1"><User className="w-3 h-3" />Patient: {event.ptName}</span>
                                {event.author && <span>Commented By: {event.author.username || 'Unknown'}</span>}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Comment added: {
                                // --- Display createdAt time in UTC to avoid local conversion ---
                                new Date(event.createdAt).toLocaleString('en-US', {
                                  year: 'numeric', month: 'numeric', day: 'numeric',
                                  hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Los_Angeles'
                                }) 
                                // --- END ---
                              }
                            </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                            <button 
                                onClick={(e) => handleEventClick(e, event)} 
                                type="button"
                                disabled={isLocked}
                                title={isLocked ? "This task is overdue and locked." : "View record details"}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-md transition-colors flex items-center gap-1 w-full justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                <FileText className="w-3 h-3" />View Record
                            </button>
                            </div>
                        </div>
                        </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No scheduled events for this date</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScheduleCalendar;
